// @flow
import React, { useState, useEffect } from 'react';
import { createUseStyles } from 'react-jss';
import cx from 'classnames';
import isVideo from 'shared/lib/isVideo';
import isImage from 'shared/lib/isImage';
import LoadingOverlay from './components/LoadingOverlay';

type Props = {
  asset: ?string,
  inAssetBrowser: ?boolean,
  // Poll the asset until it is available - then show it
  shouldPoll?: boolean
}

const AssetPreview = (props: Props) => {
  const { asset, shouldPoll } = props;
  const c = useStyles(props);
  const [loaded, setLoaded] = useState(!shouldPoll);

  // Poll until the asset is loaded
  useEffect(() => {
    // No action needed if no asset or if asset is loaded
    if (loaded || !asset) return;

    let count = 1;

    // Poll for 30 seconds - once at 0s, then every 5s
    const POLL_FREQUENCY = 5000;
    const MAX_POLLS = 7;

    const fetchImageAvailability = async () => {
      if (count >= MAX_POLLS) {
        setLoaded(true);
      }

      const res = await fetch(asset, {
        // Only check availability - don't download
        method: 'HEAD',
        // Get uncached response
        cache: 'no-store'
      });

      // OK = asset is available; otherwise keep polling
      if (res.ok) {
        setLoaded(true);
      } else {
        count += 1;
      }
    };
    // initial call - async
    fetchImageAvailability();

    const interval = setInterval(fetchImageAvailability, POLL_FREQUENCY);

    return () => clearInterval(interval);
  }, [asset, loaded]);

  if (!asset) {
    return null;
  }

  return (
    <div className={c.container}>
      {
        isVideo(asset) && (
          <div className={c.videoContainer}>
            <video
              className={c.video}
              controls
              key={asset}
            >
              <source src={asset} />
            </video>
          </div>
        )
      }
      {
        isImage(asset) && (
          <div className={cx(c.image, { [c.showImage]: loaded })} />
        )
      }
      {
        !isVideo(asset) && !isImage(asset) && (
          <div>Preview not available for this asset</div>
        )
      }
      {
        !loaded && (
          <LoadingOverlay
            animationSize={60}
            animationThickness={8}
          />
        )
      }
    </div>
  );
};

const useStyles = createUseStyles({
  container: {
    flexShrink: 0,
    height: '100%',
    top: '10px',
    width: '40%',
    position: ({ inAssetBrowser }) => inAssetBrowser ? 'sticky' : 'relative',
    right: ({ inAssetBrowser }) => (inAssetBrowser && '32px')
  },
  videoContainer: {
    height: '100%',
    width: '100%'
  },
  video: {
    width: 'inherit',
    height: 'auto'
  },
  image: {
    minHeight: ({ inAssetBrowser }) => (inAssetBrowser ? '400px' : '200px'),
  },
  showImage: {
    backgroundImage: ({ asset }) => `url(${asset})`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'contain',
    backgroundPosition: 'center center',
    backgroundColor: 'lightgray',
  },
});

export default AssetPreview;
